import { FC, useState, useCallback } from 'react';
import { KatButton } from '@amzn/katal-react';
import { MarketPlace, TransformationVariant } from '@amzn/fire-tv-campaign-automation-service';
import {
  MultiImageInput,
  ImageEntry,
  ImageValidationSpec,
} from 'src/components/general/MultiImageInput';
import { OverlaySection } from './OverlaySection';
import { AssetState, AssetStatus } from 'src/types';
import { MULTI_TITLE_HORIZONTAL_CONSTRAINTS } from 'src/constants';
import { isFeatureEnabled, FeatureFlag } from 'src/featureFlags';
import styles from './MultiTitleHorizontalInput.module.css';

export interface MultiTitleHorizontalInputProps {
  disabled: boolean;
  assetState: AssetState;
  marketplace: MarketPlace;
  setState: (state: Partial<AssetState>) => void;
  onGenerate: (images: ImageEntry[]) => void;
  onCancel: () => void;
}

const VALIDATION_SPEC: ImageValidationSpec = {
  maxFileSizeBytes: 10 * 1024 * 1024,
  minWidth: 1280,
  minHeight: 720,
  aspectRatio: { width: 16, height: 9 },
  acceptedFileTypes: MULTI_TITLE_HORIZONTAL_CONSTRAINTS.ACCEPTED_IMAGE_TYPES,
};

/**
 * Input component for the Multi Title Horizontal transformation variant.
 * Supports 1-3 images with drag-and-drop reorder, overlay controls, and generate/cancel actions.
 */
export const MultiTitleHorizontalInput: FC<MultiTitleHorizontalInputProps> = ({
  disabled,
  assetState,
  marketplace,
  setState,
  onGenerate,
  onCancel,
}) => {
  const [images, setImages] = useState<ImageEntry[]>([]);

  const handleImagesChange = useCallback((newImages: ImageEntry[]) => {
    setImages(newImages);
  }, []);

  const canGenerate =
    images.length >= MULTI_TITLE_HORIZONTAL_CONSTRAINTS.MIN_IMAGES &&
    images.length <= MULTI_TITLE_HORIZONTAL_CONSTRAINTS.MAX_IMAGES;

  const handleGenerate = () => {
    if (canGenerate) {
      onGenerate(images);
    }
  };

  const overlayEnabled = assetState.status === AssetStatus.COMPLETED && !!assetState.key;

  return (
    <div className={styles.container} data-testid="multi-title-horizontal-input">
      <MultiImageInput
        images={images}
        onImagesChange={handleImagesChange}
        maxImages={MULTI_TITLE_HORIZONTAL_CONSTRAINTS.MAX_IMAGES}
        validationSpec={VALIDATION_SPEC}
        disabled={disabled}
        reorderHint="Drag to reorder images. Order determines tile position in the output."
        data-testid="mth-multi-image"
      />

      {isFeatureEnabled(FeatureFlag.OverlaySection) && (
        <OverlaySection
          assetState={assetState}
          marketplace={marketplace}
          enabled={overlayEnabled}
          setState={setState}
        />
      )}

      <div className={styles.actions}>
        <KatButton
          variant="tertiary"
          onClick={onCancel}
          disabled={disabled}
          data-testid="mth-cancel-button"
        >
          Cancel
        </KatButton>
        <KatButton
          variant="primary"
          onClick={handleGenerate}
          disabled={disabled || !canGenerate}
          data-testid="mth-generate-button"
        >
          Generate
        </KatButton>
      </div>
    </div>
  );
};
